import classNames from 'classnames'
import { Dialog, InlineDialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import type { FieldError } from 'react-hook-form'
import { BreakoutDateTimePicker } from './BreakoutDateTimePicker'
import { FormError } from './form/FormError'
import { FormLabel } from './form/FormLabel'
import type { FormFieldDefaults } from './types'
import { CalendarIcon } from 'components/icons/Calendar'

/** 1min resolution has a huge dropdown so is intended for internal use only. */
export type DateTimeResolution = '1min' | '15min' | '1day'

// eslint-disable-next-line react-refresh/only-export-components
export const FixedTimeComponents = {
  midnight: { hour: 0, minute: 0 },
  endOfDay: { hour: 23, minute: 59 },
}

export type BreakoutDateTimeProps = Omit<
  FormFieldDefaults<'input'>,
  'value' | 'onChange' | 'max' | 'min' | 'defaultValue'
> & {
  kind?: 'secondary' | 'tertiary'
  LeadingIcon?: React.ComponentType<
    { size: number } & React.SVGProps<SVGSVGElement>
  >
  iconClassName?: string
  inputClassName?: string
  min?: DateTime
  max?: DateTime
  emptyText?: string
  value?: DateTime | null
  initialValue?: DateTime | null
  defaultValue?: DateTime
  hideNowButton?: boolean
  initialView?: DateTime
  force12HourMode?: boolean
  showInaccessibleTimes?: boolean
  onChange?: (value: DateTime | null) => void
} & (
    | {
        resolution: '1day'
        fixedTimeComponent: { hour: number; minute: number }
      }
    | {
        resolution: Exclude<DateTimeResolution, '1day'>
        fixedTimeComponent?: never
      }
  )

export const BreakoutDateTimeInput = forwardRef<
  HTMLInputElement,
  BreakoutDateTimeProps
>(function BreakoutDateTimeInput(props, fwdRef) {
  const {
    value: _value,
    defaultValue,
    onChange,
    min,
    max,
    error,
    errorClass,
    className,
    disabled,
    hideNowButton,
    placeholder: _placeholder,
    label,
    kind = 'tertiary',
    name,
    labelClass,
    required,
    initialView,
    initialValue,
    force12HourMode,
    resolution,
    fixedTimeComponent,
    showInaccessibleTimes,
  } = props

  const { tt } = useTranslationTyped()
  const placeholder = _placeholder ?? tt.design_system.select_date()
  const divRef = useRef<HTMLDivElement>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [innerValue, setInnerValue] = useState<DateTime | null | undefined>(
    defaultValue ? defaultValue : null
  )

  // Update innerValue when controlled value changes
  useEffect(() => {
    if (_value === undefined) return
    setInnerValue(_value)
  }, [_value])

  // Use controlled value if provided, otherwise use internal state
  const value = _value !== undefined ? _value : innerValue

  const innerRef = useRef<HTMLInputElement | null>(null)

  return (
    <div className="flex flex-col items-start gap-0.5">
      {label && (
        <div>
          <FormLabel
            required={required}
            label={label}
            labelClass={labelClass}
          />
        </div>
      )}
      <div className="relative h-0 w-0 overflow-hidden">
        <input
          ref={(e) => {
            innerRef.current = e
            if (!fwdRef) {
              return
            }

            if (typeof fwdRef === 'function') {
              fwdRef(e)
              return
            } else {
              fwdRef.current = e
            }
          }}
          name={name}
          disabled={disabled}
          data-testid={`datetime-input-${name}`}
          type="datetime-local"
          className="absolute hidden opacity-0"
          onChange={(e) => {
            const datetime = e.target.value
              ? DateTime.fromISO(e.target.value)
              : null
            setInnerValue(datetime)
            onChange?.(datetime)
          }}
          value={
            value
              ? value.toFormat('yyyy-LL-dd') + 'T' + value.toFormat('HH:mm')
              : ''
          }
        />
      </div>
      <div
        tabIndex={0}
        aria-label={[
          label,
          value ? getFormattedDateTime(value, resolution) : placeholder,
        ].join(': ')}
        role="button"
        aria-haspopup="dialog"
        data-testid={`datetime-field-${name}`}
        onKeyDown={(e) => {
          if (disabled) return
          if (e.key === 'Enter' || e.key === ' ') {
            setDialogOpen(true)
          }
        }}
        onClick={() => {
          if (disabled) return
          setDialogOpen(true)
        }}
        ref={divRef}
        className={classNames(
          'group flex h-[3.25rem] w-full select-none flex-row items-center justify-between rounded-2xl px-4 py-1 text-label-medium outline outline-1 outline-outline-variant',
          className,
          {
            'bg-core-secondary': kind === 'secondary' && !disabled,
            'bg-core-tertiary': kind === 'tertiary' && !disabled,
            'bg-surface-dim': kind === 'secondary' && disabled,
            'bg-surface': kind === 'tertiary' && disabled,
            'text-on-surface-disabled': disabled,
            'focus:outline-none focus:ring-2 focus:ring-on-surface-disabled md:focus:text-xs':
              !disabled,
            'cursor-not-allowed': disabled,
            'cursor-pointer': !disabled,
          }
        )}
      >
        <div
          className={classNames('inline-flex items-center gap-1', {
            'text-on-surface-disabled': !value,
          })}
        >
          <CalendarIcon size={12} className="mb-[1px]" />
          {value ? getFormattedDateTime(value, resolution) : placeholder}
        </div>
        {!disabled && (
          <div
            className="-mr-2 p-2"
            data-testid={`datetime-clear-${name}`}
            onClick={(e) => {
              if (disabled) return

              e.preventDefault()
              e.stopPropagation()
              setInnerValue(null)
              onChange?.(null)
            }}
          >
            {!value ? null : (
              <strong className="clear-date text-body-medium lowercase text-on-surface-var hover:text-core-error">
                {tt.inputs.clear()}
              </strong>
            )}
          </div>
        )}
      </div>
      <DatePickerDialog
        force12HourMode={force12HourMode}
        header={label}
        min={min}
        error={error}
        max={max}
        content={tt.design_system.confirm_date_change()}
        show={dialogOpen}
        value={value ?? null}
        initialValue={initialValue ?? undefined}
        initialView={initialView}
        hideNowButton={hideNowButton}
        onChange={(datetime) => {
          onChange?.(datetime)
          setInnerValue(datetime)
          setDialogOpen(false)
          divRef.current?.focus()
        }}
        onCancel={() => {
          setDialogOpen(false)
          divRef.current?.focus()
        }}
        resolution={resolution}
        fixedTimeComponent={fixedTimeComponent}
        showInaccessibleTimes={showInaccessibleTimes}
      />
      <FormError error={error} errorClass={errorClass} />
    </div>
  )
})

function getFormattedDateTime(
  dateTime: DateTime,
  resolution: DateTimeResolution
) {
  if (resolution === '1day') {
    return dateTime.toLocaleString(DateTime.DATE_FULL)
  }
  return dateTime.toLocaleString(DateTime.DATETIME_FULL)
}

export function DatePickerDialog({
  value,
  show,
  hideNowButton,
  onChange,
  onCancel,
  min,
  error,
  max,
  header,
  initialView,
  initialValue,
  force12HourMode,
  resolution = '15min',
  transitionDuration,
  useInlineDialog = true,
  fixedTimeComponent,
  showInaccessibleTimes,
}: {
  value: DateTime | null
  content: string
  show: boolean
  hideNowButton?: boolean
  error: FieldError | undefined
  onChange: (dateTime: DateTime | null) => void
  onCancel: () => void
  header?: string
  min?: DateTime
  max?: DateTime
  initialView?: DateTime
  initialValue?: DateTime
  force12HourMode?: boolean
  transitionDuration?: number
  useInlineDialog?: boolean
  resolution?: DateTimeResolution
  fixedTimeComponent?: { hour: number; minute: number }
  showInaccessibleTimes?: boolean
}) {
  const DialogToUse = useInlineDialog ? InlineDialog : Dialog
  const { tt } = useTranslationTyped()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const ref = useRef<HTMLDivElement>(null)

  const focusLastTabbable = useCallback(() => {
    const div = ref.current
    if (!div) return

    setTimeout(() => {
      const tabbables: HTMLElement[] = []

      // find all tabbable elements in the dialog context
      div
        .querySelectorAll(
          '[tabindex]:not([tabindex="-1"]), button:not([disabled]), [href], input, select, textarea'
        )
        .forEach((el) => {
          tabbables.push(el as HTMLElement)
        })

      const last = tabbables[tabbables.length - 1]
      last?.focus()
    }, 16) // 1 frame
  }, [])

  return (
    <DialogToUse
      open={show}
      onDismiss={() => onCancel()}
      size="custom"
      className="relative max-w-[400px] rounded-3xl bg-surface-bright p-13"
      transitionDuration={transitionDuration}
      dismissOnEscapeKey
      testId="datetime-dialog"
      aria-label={tt.inputs.date_time_picker()}
    >
      <DialogCloseButton
        buttonRef={buttonRef}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onCancel()
          return false
        }}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && e.shiftKey) {
            focusLastTabbable()
          }
        }}
      />
      {header !== undefined && (
        <div className="mb-3 text-left text-headline-medium">{header}</div>
      )}
      <div className="text-center text-body-large" ref={ref}>
        <BreakoutDateTimePicker
          error={error}
          initialView={initialView}
          hideNowButton={hideNowButton}
          min={min}
          max={max}
          onChange={onChange}
          value={value || initialValue}
          force12HourMode={force12HourMode}
          resolution={resolution}
          fixedTimeComponent={fixedTimeComponent}
          focusOutRef={buttonRef}
          showInaccessibleTimes={showInaccessibleTimes}
        />
      </div>
    </DialogToUse>
  )
}
